import React from 'react';
import { i18n } from 'config/locales';
import { Summary, DateSummary, NameSummary } from 'components/Summary';
import { MilitaryForeignItemModel } from 'forms/sections/military/foreign/types';
import {
  CommonOnError,
  CommonOnUpdate,
  CommonScrollIntoView,
  CommonRequired,
  CommonAddressBooks,
} from 'types/props';
import ValidationElement, {
  ValidationElementProps,
} from 'components/Form/ValidationElement/ValidationElement';
import { Dispatch } from 'redux';
import Field from 'components/Form/Field';
import RadioGroup from 'components/Form/RadioGroup';
import Radio from 'components/Form/Radio';
import DateRange from 'components/Form/DateRange';
import Country from 'components/Form/Country';
import Textarea from 'components/Form/Textarea';
import Show from 'components/Form/Show';
import Branch from 'components/Form/Branch';
import Accordion from 'components/Form/Accordion';
import Text from 'components/Form/Text';
import Comments from 'components/Form/Comments';
import { getDuplicateNamesAndAddresses } from 'helpers/people';
import { foreignServiceAddressNameFields } from 'constants/enums/people';
import ErrorMessage from 'components/ErrorMessage';
import ForeignContact from './ForeignContact';

interface Props extends ValidationElementProps, Partial<MilitaryForeignItemModel> {
  onUpdate?: CommonOnUpdate
  onError?: CommonOnError
  className?: string
  scrollIntoView?: CommonScrollIntoView
  required?: CommonRequired
  attribute?: string
  requireMaintainsContact?: boolean
  errors?: any
  addressBooks?: CommonAddressBooks
  dispatch?: Dispatch
  defaultState?: any
}

export default class ForeignService extends ValidationElement<Props> {
  constructor(props) {
    super(props);
    this.updateCountryFocused = this.updateCountryFocused.bind(this);
  }

  update = (queue: Partial<MilitaryForeignItemModel>) => {
    this.props.onUpdate({
      Organization: this.props.Organization,
      Name: this.props.Name,
      Dates: this.props.Dates,
      Country: this.props.Country,
      Rank: this.props.Rank,
      Division: this.props.Division,
      Circumstances: this.props.Circumstances,
      ReasonLeft: this.props.ReasonLeft,
      MaintainsContact: this.props.MaintainsContact,
      List: this.props.List,
      Comments: this.props.Comments,
      ContactAnswerComments: this.props.ContactAnswerComments,
      ContactSummaryComments: this.props.ContactSummaryComments,
      CountryFocused: this.props.CountryFocused,
      ...queue,
    });
  };

  updateOrganization = (value: MilitaryForeignItemModel['Organization']) => {
    this.update({
      Organization: value,
    });
  };

  updateName = (value: MilitaryForeignItemModel['Name']) => {
    this.update({
      Name: value,
    });
  };

  updateDates = (value: MilitaryForeignItemModel['Dates']) => {
    this.update({
      Dates: value,
    });
  };

  updateCountry = (value: MilitaryForeignItemModel['Country']) => {
    this.update({
      Country: value,
    });
  };

  updateRank = (value: MilitaryForeignItemModel['Rank']) => {
    this.update({
      Rank: value,
    });
  };

  updateDivision = (value: MilitaryForeignItemModel['Division']) => {
    this.update({
      Division: value,
    });
  };

  updateCircumstances = (value: MilitaryForeignItemModel['Circumstances']) => {
    this.update({
      Circumstances: value,
    });
  };

  updateReasonLeft = (value: MilitaryForeignItemModel['ReasonLeft']) => {
    this.update({
      ReasonLeft: value,
    });
  };

  updateMaintainsContact = (values: MilitaryForeignItemModel['MaintainsContact']) => {
    // If there is no history clear out any previously entered data
    this.update({
      MaintainsContact: values,
      List: values.value === 'Yes' ? this.props.List : [],
    });
  };

  updateList = (values: MilitaryForeignItemModel['List']) => {
    this.update({
      List: values,
    });
  };

  updateComments = (values: MilitaryForeignItemModel['Comments']) => {
    this.update({
      Comments: values,
    });
  };

  updateContactAnswerComments = (values: MilitaryForeignItemModel['ContactAnswerComments']) => {
    this.update({
      ContactAnswerComments: values,
    });
  };

  updateContactSummaryComments = (values: MilitaryForeignItemModel['ContactSummaryComments']) => {
    this.update({
      ContactSummaryComments: values,
    });
  };

  updateCountryFocused(focus: boolean) {
    this.update({
      CountryFocused: focus,
    });
  }

  /**
   * Assists in rendering the summary section.
   */
  summary = (item, index) => {
    const itemProperties = (item || {}).Item || {};
    const dates = DateSummary(itemProperties.Dates);
    const name = NameSummary(itemProperties.Name);

    return Summary({
      type: i18n.t('military.foreign.collection.contacts.summary.item'),
      index,
      left: name,
      right: dates,
      placeholder: i18n.t('military.foreign.collection.contacts.summary.unknown'),
    });
  };

  render() {
    const {
      requireMaintainsContact, List, peopleWhoKnowYou, errors
    } = this.props;
    const accordionErrors = errors && errors.filter((e) => e.indexOf('List.accordion') === 0);
    const attribute = `List.items[${this.props.$index}].Item`;

    const duplicatePeopleWhoKnowYou = getDuplicateNamesAndAddresses(List?.items, peopleWhoKnowYou, foreignServiceAddressNameFields);

    const duplicatePeopleWhoKnowYouError = (
      <Field
        className="no-margin-bottom"
        title={i18n.t('relationships.people.error.duplicatePeopleWhoKnowYouError.title')}
        scrollIntoView={this.props.scrollIntoView}
        hideTitle
      >
        <ErrorMessage
          title={i18n.t('relationships.people.error.duplicatePeopleWhoKnowYouError.title')}
          message={[
            i18n.t('relationships.people.error.duplicatePeopleWhoKnowYouError.message'),
            ...duplicatePeopleWhoKnowYou.errors.map((item) => ` - ${item}`)
          ]}
        />
      </Field>
    );

    const duplicatePeopleWhoKnowYouWarning = (
      <ErrorMessage
        isWarning
        title={i18n.t('relationships.people.error.duplicatePeopleWhoKnowYouWarning.title')}
        message={[
          i18n.t('relationships.people.error.duplicatePeopleWhoKnowYouWarning.message'),
          ...duplicatePeopleWhoKnowYou.warnings.map((item) => ` - ${item}`)
        ]}
      />
    );

    return (
      <div className="foreign-service">
        <Field
          title={i18n.t('military.foreign.heading.organization')}
          adjustFor="big-buttons"
          shrink
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.Organization`}
        >
          <RadioGroup
            className="organization option-list option-list-vertical"
            onError={this.props.onError}
            required={this.props.required}
            selectedValue={(this.props.Organization || {}).value}
            attribute={`${attribute}.Organization`}
          >
            <Radio
              name="organization-military"
              className="organization-military"
              label={i18n.t('military.foreign.label.organization.military')}
              value="Military"
              onUpdate={this.updateOrganization}
              onError={this.props.onError}
            />
            <Radio
              name="organization-intelligence"
              className="organization-intelligence"
              label={i18n.t('military.foreign.label.organization.intelligence')}
              value="Intelligence"
              onUpdate={this.updateOrganization}
              onError={this.props.onError}
            />
            <Radio
              name="organization-diplomatic"
              className="organization-diplomatic"
              label={i18n.t('military.foreign.label.organization.diplomatic')}
              value="Diplomatic"
              onUpdate={this.updateOrganization}
              onError={this.props.onError}
            />
            <Radio
              name="organization-security"
              className="organization-security"
              label={i18n.t('military.foreign.label.organization.security')}
              value="Security"
              onUpdate={this.updateOrganization}
              onError={this.props.onError}
            />
            <Radio
              name="organization-militia"
              className="organization-militia"
              label={i18n.t('military.foreign.label.organization.militia')}
              value="Militia"
              onUpdate={this.updateOrganization}
              onError={this.props.onError}
            />
            <Radio
              name="organization-defense"
              className="organization-defense"
              label={i18n.t('military.foreign.label.organization.defense')}
              value="OtherDefense"
              onUpdate={this.updateOrganization}
              onError={this.props.onError}
            />
            <Radio
              name="organization-other"
              className="organization-other"
              label={i18n.t('military.foreign.label.organization.other')}
              value="OtherGov"
              onUpdate={this.updateOrganization}
              onError={this.props.onError}
            />
          </RadioGroup>
        </Field>

        <Field
          title={i18n.t('military.foreign.heading.name')}
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.Name`}
        >
          <Text
            name="Name"
            ariaLabel={i18n.t('military.foreign.heading.name')}
            {...this.props.Name}
            className="foreign-service-name"
            maxlength="100"
            onUpdate={this.updateName}
            onError={this.props.onError}
            required={this.props.required}
            attribute={`${attribute}.Name`}
          />
        </Field>

        <Field
          title={i18n.t('military.foreign.heading.dates')}
          help="military.foreign.help.dates"
          adjustFor="daterange"
          shrink
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.Dates`}
        >
          <DateRange
            name="Dates"
            className="foreign-service-dates"
            minDateEqualTo
            {...this.props.Dates}
            onUpdate={this.updateDates}
            onError={this.props.onError}
            required={this.props.required}
            hideFromDay
            hideToDay
            attribute={`${attribute}.Dates`}
          />
        </Field>

        <Field
          title={i18n.t('military.foreign.heading.country')}
          adjustFor="country"
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.Country`}
        >
          <Country
            name="Country"
            ariaLabel={i18n.t('military.foreign.heading.country')}
            {...this.props.Country}
            excludeUnitedStates
            className="foreign-service-country"
            maxlength="100"
            onUpdate={this.updateCountry}
            updateCountryFocused={this.updateCountryFocused}
            onError={this.props.onError}
            required={this.props.required}
            attribute={`${attribute}.Country`}
          />
        </Field>

        <Field
          title={i18n.t('military.foreign.heading.rank')}
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.Rank`}
        >
          <Text
            name="Rank"
            ariaLabel={i18n.t('military.foreign.heading.rank')}
            {...this.props.Rank}
            className="foreign-service-rank"
            maxlength="100"
            onUpdate={this.updateRank}
            onError={this.props.onError}
            required={this.props.required}
            attribute={`${attribute}.Rank`}
          />
        </Field>

        <Field
          title={i18n.t('military.foreign.heading.division')}
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.Division`}
        >
          <Text
            name="Division"
            {...this.props.Division}
            className="foreign-service-division"
            maxlength="100"
            onUpdate={this.updateDivision}
            onError={this.props.onError}
            required={this.props.required}
            attribute={`${attribute}.Division`}
          />
        </Field>

        <Field
          title={i18n.t('military.foreign.heading.circumstances')}
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.Circumstances`}
        >
          <Textarea
            name="Circumstances"
            {...this.props.Circumstances}
            className="foreign-service-circumstances"
            maxlength="100"
            onUpdate={this.updateCircumstances}
            onError={this.props.onError}
            required={this.props.required}
            attribute={`${attribute}.Circumstances`}
          />
        </Field>

        <Field
          title={i18n.t('military.foreign.heading.left')}
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.ReasonLeft`}
        >
          <Textarea
            name="ReasonLeft"
            {...this.props.ReasonLeft}
            className="foreign-service-left"
            maxlength="100"
            onUpdate={this.updateReasonLeft}
            onError={this.props.onError}
            required={this.props.required}
            attribute={`${attribute}.ReasonLeft`}
          />
        </Field>
        <Comments
          title={i18n.t('military.foreign.heading.comments')}
          name="EntryComments"
          attribute="Comments"
          className="entry-comments"
          {...this.props.Comments}
          onUpdate={this.updateComments}
          onError={this.props.onError}
          required={false}
        />
        <Show when={requireMaintainsContact}>
          <Branch
            name="has_maintainscontact"
            label={i18n.t('military.foreign.heading.maintainscontact')}
            labelSize="h4"
            className="maintainscontact"
            {...this.props.MaintainsContact}
            help="military.foreign.help.maintainscontact"
            onUpdate={this.updateMaintainsContact}
            required={this.props.required}
            scrollIntoView={this.props.scrollIntoView}
            onError={this.props.onError}
            attribute={`${attribute}.MaintainsContact`}
          />
          <Comments
            title={i18n.t('military.foreign.heading.contactanswercomments')}
            name="ContactAnswerComments"
            attribute="ContactAnswerComments"
            className="contact-answer-comments"
            {...this.props.ContactAnswerComments}
            onUpdate={this.updateContactAnswerComments}
            onError={this.props.onError}
            required={false}
          />
          <Show when={this.props.MaintainsContact?.value === 'Yes'}>
            <div>
              <Field
                title={i18n.t('military.foreign.heading.contact.details')}
                titleSize="h3"
                optional
                className="no-margin-bottom"
              >
                {i18n.t('military.foreign.para.contact')}
              </Field>

              <Accordion
                className="foreign-contacts-collection"
                {...this.props.List}
                defaultState={this.props.defaultState}
                onUpdate={this.updateList}
                onError={this.props.onError}
                errors={accordionErrors}
                summary={this.summary}
                description={i18n.t('military.foreign.collection.contacts.summary.title')}
                appendTitle={i18n.t('military.foreign.collection.contacts.appendTitle')}
                appendLabel={i18n.t('military.foreign.collection.contacts.append')}
                required={this.props.required}
                scrollIntoView={this.props.scrollIntoView}
                additionalErrors={
                  (duplicatePeopleWhoKnowYou.errors.length > 0 && duplicatePeopleWhoKnowYouError)
                  || (duplicatePeopleWhoKnowYou.warnings.length > 0 && duplicatePeopleWhoKnowYouWarning)
                }
              >
                <ForeignContact
                  attribute={attribute}
                  name="Item"
                  bind
                  addressBooks={this.props.addressBooks}
                  dispatch={this.props.dispatch}
                  required={this.props.required}
                  scrollIntoView={this.props.scrollIntoView}
                />
              </Accordion>
              <Comments
                title={i18n.t('military.foreign.heading.contactsummarycomments')}
                name="ContactSummaryComments"
                attribute="ContactSummaryComments"
                className="contact-summary-comments"
                {...this.props.ContactSummaryComments}
                onUpdate={this.updateContactSummaryComments}
                onError={this.props.onError}
                required={false}
              />
            </div>
          </Show>
        </Show>
      </div>
    );
  }
}

ForeignService.defaultProps = {
  MaintainsContact: {},
  addressBooks: {},
  dispatch: () => { },
  onUpdate: () => { },
  Comments: {},
  ContactAnswerComments: {},
  ContactSummaryComments: {},
  onError: (value, arr) => arr,
  CountryFocused: false,
  defaultState: true,
  requireMaintainsContact: true,
};
